import { PRODUCT } from "@/config/product";
import { getAttribution } from "@/lib/attribution";

// Minimal Shopify Storefront API client (public token — safe for browser).
// All Shopify logic lives here + config/product.js; UI components stay clean.

const DOMAIN = process.env.REACT_APP_SHOPIFY_STORE_DOMAIN;
const TOKEN = process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN;
const API_VERSION = process.env.REACT_APP_SHOPIFY_API_VERSION;

async function storefrontFetch(query, variables = {}) {
  const res = await fetch(`https://${DOMAIN}/api/${API_VERSION}/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error(`Storefront API HTTP ${res.status}`);
  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(json.errors.map((e) => e.message).join("; "));
  }
  return json.data;
}

// colour name + US size -> { id, availableForSale }, fetched once per session.
let variantCache = null;
export async function getVariantMap() {
  if (variantCache) return variantCache;
  const data = await storefrontFetch(
    `query ($handle: String!) {
      product(handle: $handle) {
        variants(first: 100) {
          nodes { id availableForSale price { amount } selectedOptions { name value } }
        }
      }
    }`,
    { handle: PRODUCT.shopify.handle }
  );
  if (!data.product) throw new Error("Product not found in Shopify");
  const map = {};
  for (const v of data.product.variants.nodes) {
    const opts = Object.fromEntries(
      v.selectedOptions.map((o) => [o.name, o.value])
    );
    const key =
      `${opts[PRODUCT.shopify.colourOption]}|${opts[PRODUCT.shopify.sizeOption]}`.toLowerCase();
    map[key] = {
      id: v.id,
      availableForSale: v.availableForSale,
      price: parseFloat(v.price.amount),
    };
  }
  variantCache = map;
  return map;
}

// lines: [{ merchandiseId, quantity }] -> hosted checkout URL.
export async function createCheckoutUrl(lines, discountCode = null) {
  const input = { lines };
  if (discountCode) input.discountCodes = [discountCode];
  // Attribution handoff: carry the Emergent session's UTMs, click IDs and
  // landing page as cart attributes. They travel cart -> checkout -> order
  // (visible on the order's Additional details). No Meta browser cookies
  // (_fbp/_fbc) and no customer PII are ever sent.
  const attrs = cartAttributionAttributes();
  if (attrs.length) input.attributes = attrs;
  const data = await storefrontFetch(
    `mutation ($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id checkoutUrl attributes { key value } }
        userErrors { field message }
      }
    }`,
    { input }
  );
  const errs = data.cartCreate.userErrors;
  if (errs?.length) throw new Error(errs.map((e) => e.message).join("; "));
  return data.cartCreate.cart.checkoutUrl;
}

// Documented Cart API mechanism: AttributeInput[]. Click IDs (fbclid/gclid)
// and UTMs are standard ad-attribution fields; nothing personal included.
function cartAttributionAttributes() {
  const a = getAttribution();
  const keys = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "fbclid",
    "gclid",
    "landing_page",
    "landing_referrer",
  ];
  return keys
    .filter((k) => a[k])
    .map((k) => ({ key: k, value: String(a[k]).slice(0, 500) }));
}

// --- Meta catalogue matching -------------------------------------------
// Meta's Shopify-synced catalogue keys products by the NUMERIC variant id
// (the trailing digits of the Storefront GID). These helpers resolve the
// already-cached variant map to those ids for pixel/CAPI content_ids.

export function numericVariantId(gid) {
  if (!gid) return null;
  const n = String(gid).split("/").pop();
  return /^\d+$/.test(n) ? n : null;
}

// colour display name + EU size ("EU 38" or "38") -> numeric variant id,
// or null when it can't be resolved (never substitutes a guessed id).
export async function getVariantContentId(colourName, euSize) {
  const eu = String(euSize).replace(/^EU\s+/i, "");
  const us = PRODUCT.shopify.euToUs[eu];
  if (!us) return null;
  const map = await getVariantMap();
  const v = map[`${colourName}|${us}`.toLowerCase()];
  return v ? numericVariantId(v.id) : null;
}

// Default variant for ViewContent: Pink + first in-stock EU size.
export async function getDefaultContentId() {
  const map = await getVariantMap();
  for (const us of Object.values(PRODUCT.shopify.euToUs)) {
    const v = map[`pink|${us}`.toLowerCase()];
    if (v?.availableForSale) return numericVariantId(v.id);
  }
  return null;
}
